/**
 * upload/UploadOverlay.tsx — the upload overlay's public entry point.
 *
 * This used to be the whole feature (state + handlers + JSX). It is now a thin DISPATCHER: the journey
 * logic lives in `useUploadOverlay.ts`, and THREE skins render it —
 *   V1 `UploadOverlayClassic`   the backend team's original navy look (the DEFAULT)
 *   V2 `UploadOverlayReskin`    the design system, composed into pages BY HAND
 *   V3 `UploadOverlayPages`     the designer's OWN page files, wired to live data
 * Which one shows is a per-browser choice read from `skinStore` (set from the profile menu in App.tsx).
 * The export name and `UploadOverlayProps` are unchanged, so every caller (Share.tsx) is unaffected —
 * only the appearance behind the toggle changes.
 *
 * BEHAVIOUR CANNOT FORK: all three skins share ONE hook (`useUploadOverlay`), so the journey,
 * validation and draft handling are the same code in any of them. Only the rendering differs.
 *
 * ONE THING V3 NEEDS THAT V1/V2 DO NOT: the flows list. V3 puts the automation picker INSIDE the
 * overlay (P1, as the design draws it), so it needs the rows to list; V1 and V2 pick outside the
 * overlay and only ever see the one chosen flow. It arrives as an OPTIONAL `flows` prop — optional so
 * the shared props type stays as it was and no caller is forced to change. When V3 is selected and the
 * list was not passed, it falls back to the single flow the caller opened for: one real row, which is
 * honest, rather than an empty table.
 */
import { useSkin } from './skinStore';
import { UploadOverlayClassic } from './UploadOverlayClassic';
import { UploadOverlayReskin } from './UploadOverlayReskin';
import { UploadOverlayPages } from './UploadOverlayPages';
import type { UploadOverlayProps } from './useUploadOverlay';
import type { CloudFlowSummary } from '../api';

export type { UploadOverlayProps } from './useUploadOverlay';

export function UploadOverlay({
  flows,
  onFlowChosen,
  ...props
}: UploadOverlayProps & {
  /** V3 only — the rows P1 lists. Ignored by V1/V2, which pick before the overlay opens. */
  flows?: CloudFlowSummary[];
  /** V3 only — P1's pick, so the caller can run its connector pre-detect for the real automation. */
  onFlowChosen?: (flow: CloudFlowSummary) => void;
}) {
  const skin = useSkin();
  if (skin === 'v3') {
    return (
      <UploadOverlayPages
        {...props}
        flows={flows ?? [props.flow]}
        {...(onFlowChosen ? { onFlowChosen } : {})}
      />
    );
  }
  return skin === 'v2' ? <UploadOverlayReskin {...props} /> : <UploadOverlayClassic {...props} />;
}
